import {
  Award,
  BookOpen,
  Flame,
  Footprints,
  Headphones,
  Lock,
  Mic,
  PenLine,
  Shapes,
  Sparkles,
  Trophy,
} from "lucide-react";
import { motion } from "framer-motion";
import BilingualText from "@/components/BilingualText";
import { cn as cx } from "@/lib/utils";

const ICONS = {
  "flame": Flame,
  "book-open": BookOpen,
  "sparkles": Sparkles,
  "shapes": Shapes,
  "headphones": Headphones,
  "pen-line": PenLine,
  "mic": Mic,
  "footprints": Footprints,
  "award": Award,
  "trophy": Trophy,
};

const TIER_STYLES = {
  bronze:   { bg: "bg-amber-100",  fg: "text-amber-800",   ring: "ring-amber-300",   label: "Bronze" },
  silver:   { bg: "bg-slate-200",  fg: "text-slate-700",   ring: "ring-slate-300",   label: "Silver" },
  gold:     { bg: "bg-yellow-100", fg: "text-yellow-800",  ring: "ring-yellow-300",  label: "Gold" },
  platinum: { bg: "bg-fuchsia-100",fg: "text-fuchsia-800", ring: "ring-fuchsia-300", label: "Platinum" },
};

function BadgeTile({ badge, index }) {
  const Icon = ICONS[badge.icon] || Award;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.gold;
  const pct = Math.round((badge.progress || 0) * 100);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.03, duration: 0.35 }}
      whileHover={{ y: -3 }}
      className={cx(
        "relative card-plush p-5 flex flex-col items-center text-center gap-3 min-h-[200px]",
        !badge.earned && "opacity-70 grayscale-[40%]",
      )}
      data-testid={`badge-tile-${badge.id}`}
      data-earned={badge.earned ? "1" : "0"}
    >
      <span
        className={cx(
          "w-16 h-16 rounded-2xl grid place-items-center ring-4 transition-all",
          badge.earned ? `${tier.bg} ${tier.fg} ${tier.ring}` : "bg-slate-100 text-slate-400 ring-slate-100",
        )}
      >
        {badge.earned ? (
          <Icon className="w-7 h-7" />
        ) : (
          <Lock className="w-5 h-5" />
        )}
      </span>
      <div className="flex flex-col items-center gap-0.5">
        <span className="cn text-sm font-black text-slate-900 leading-tight">
          {badge.cn}
        </span>
        <span className="text-xs font-bold text-slate-500 leading-tight">
          {badge.en}
        </span>
      </div>

      {/* Description */}
      <p className="text-[11px] text-slate-500 leading-snug px-1">
        <span className="cn block">{badge.description_cn}</span>
        <span className="block">{badge.description_en}</span>
      </p>

      {/* Progress bar */}
      {!badge.earned && badge.progress > 0 && (
        <div className="w-full mt-auto">
          <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-sky-400 to-emerald-400"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[10px] text-slate-400 mt-1 block">{pct}%</span>
        </div>
      )}

      {badge.earned && (
        <span className={cx("badge-soft mt-auto", tier.bg, tier.fg)}>
          {tier.label} · Earned
        </span>
      )}
    </motion.div>
  );
}

/**
 * Full-grid badge display. Grouped by category (streak / mastery / milestone).
 */
export function BadgesGrid({ badges = [] }) {
  const streak = badges.filter((b) => b.category === "streak");
  const mastery = badges.filter((b) => b.category === "mastery");
  const milestone = badges.filter((b) => b.category === "milestone");

  const groups = [
    { key: "streak",    cn: "连击徽章",  en: "Streak badges",    items: streak },
    { key: "mastery",   cn: "技能徽章",  en: "Skill mastery",    items: mastery },
    { key: "milestone", cn: "里程碑",    en: "Milestones",       items: milestone },
  ];

  let idx = 0;
  return (
    <div className="space-y-8" data-testid="badges-grid">
      {groups.map((g) => (
        <div key={g.key}>
          <div className="flex items-baseline justify-between mb-4">
            <BilingualText cn={g.cn} en={g.en} size="md" />
            <span className="text-xs text-slate-400 font-bold">
              {g.items.filter((b) => b.earned).length} / {g.items.length} · 已解锁
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {g.items.map((b) => {
              const tile = <BadgeTile key={b.id} badge={b} index={idx} />;
              idx += 1;
              return tile;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Compact strip of the 3 most-recently-earned badges (for the dashboard).
 */
export function RecentBadges({ latest = [] }) {
  if (!latest.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="recent-badges">
      {latest.map((b) => {
        const Icon = ICONS[b.icon] || Award;
        const tier = TIER_STYLES[b.tier] || TIER_STYLES.gold;
        return (
          <span
            key={b.id}
            className={cx(
              "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ring-2",
              tier.bg, tier.fg, tier.ring,
            )}
            data-testid={`recent-badge-${b.id}`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="cn">{b.cn}</span>
            <span>· {b.en}</span>
          </span>
        );
      })}
    </div>
  );
}

export default BadgesGrid;
